import { Button, Flex, Form, notification } from "antd";
import { type FC, useCallback, useEffect, useMemo, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import {
    getContragentsGetByCode,
    postContragentsChangeAlias,
} from "tm-shared/api";
import { validationMessage } from "tm-shared/constants";
import { Contragent } from "tm-shared/types";

import BaseButton from "@/components/BaseButton";
import { BaseModal } from "@/components/BaseModal";
import { RHFTextField } from "@/components/RHF/RHFTextField";
import { PrimaryText } from "@/components/Typography/Styled";
import { InfoCard } from "@/components/UI/InfoCard";
import { useAppDispatch } from "@/hooks/redux";
import {
    setMarketCreatePageContragentsMap,
    setMarketCreatePageModal,
    setMarketCreatePageModalSelectionPayload,
} from "@/store/slices/ui";
import { NewLegalEntityFormValues } from "@/types/form";
import { MarketCreateModal } from "@/types/modals";

interface AddLegalEntityModalProps {
    onClose: () => void;
}

const TYPE_VOCABULARY = {
    fop: "Фізична особа-підприємець",
    company: "Юридична особа",
    custom: "Інший тип",
};

export const AddLegalEntityModal: FC<AddLegalEntityModalProps> = ({
    onClose,
}) => {
    const [legalEntity, setLegalEntity] = useState<Contragent | null>(null);
    const [status, setStatus] = useState<"success" | "error" | null>(null);
    const [loading, setLoading] = useState<boolean>(false);

    const dispatch = useAppDispatch();

    const emptyForm: NewLegalEntityFormValues = useMemo(
        () => ({
            code: "",
            alias: "",
        }),
        [],
    );

    const addLegalEntityForm = useForm<NewLegalEntityFormValues>({
        mode: "onTouched",
        reValidateMode: "onChange",
        defaultValues: {
            code: "",
        },
    });

    const { handleSubmit, reset, control } = addLegalEntityForm;

    const onSubmit = useCallback(
        async ({ code, alias }: NewLegalEntityFormValues) => {
            if (!legalEntity) {
                setLoading(true);
                try {
                    const response = await getContragentsGetByCode({ code });

                    setLegalEntity(response.result);
                    setStatus("success");
                } catch (error) {
                    setStatus("error");
                } finally {
                    setLoading(false);
                }

                return;
            }

            let label = legalEntity.fullName;

            if (alias) {
                try {
                    setLoading(true);

                    await postContragentsChangeAlias({
                        contragent: legalEntity.id,
                        alias: alias.trim(),
                    });

                    label += ` (${alias.trim()})`;
                } catch (error) {
                    console.error(error);

                    notification.error({
                        message: "Не вдалося встановити іншу назву.",
                    });

                    return;
                } finally {
                    setLoading(false);
                }
            }

            reset(emptyForm);
            onClose();

            dispatch(
                setMarketCreatePageModalSelectionPayload({
                    fieldName: "legalEntity",
                    option: {
                        label,
                        value: legalEntity.id,
                    },
                    value: legalEntity.id,
                }),
            );

            dispatch(
                setMarketCreatePageContragentsMap({
                    [legalEntity.id]: legalEntity,
                }),
            );

            return;
        },
        [dispatch, emptyForm, legalEntity, onClose, reset],
    );

    const code = useWatch({ name: "code", control });

    useEffect(() => {
        setLegalEntity(null);
        setStatus(null);
    }, [code]);

    const handleCreateIllegal = useCallback(() => {
        dispatch(
            setMarketCreatePageModal({
                type: MarketCreateModal.AddIllegalEntity,
            }),
        );
    }, [dispatch]);

    return (
        <BaseModal
            open
            onClose={onClose}
            actions={null}
            title={"Додати юридичну особу"}
            maskClosable={false}
        >
            <PrimaryText>
                Для додавання юридичної особи, заповніть форму
            </PrimaryText>

            <FormProvider {...addLegalEntityForm}>
                <Form
                    onFinish={handleSubmit(onSubmit)}
                    layout="vertical"
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 16,
                    }}
                >
                    <Flex vertical gap={12}>
                        <RHFTextField
                            name={"code"}
                            label={"ІНН/ЄДРПОУ"}
                            rules={{
                                required: validationMessage.required,
                                validate: (v: string) =>
                                    /^(?:\d{8}|\d{10})$/.test(
                                        (v ?? "").trim(),
                                    ) || "Введіть 8 (ЄДРПОУ) або 10 (ІПН) цифр",
                            }}
                            disabled={loading}
                            size="large"
                        />

                        {legalEntity && (
                            <RHFTextField
                                name={"alias"}
                                label={"Інша назва"}
                                rules={{
                                    validate: (v: string) => {
                                        if (v?.length > 0) {
                                            return v.trim().length === 0
                                                ? validationMessage.onlySpaces
                                                : true;
                                        }
                                    },
                                }}
                                disabled={loading}
                                size="large"
                            />
                        )}

                        {status !== null && (
                            <InfoCard status={status}>
                                {legalEntity ? (
                                    <Flex vertical gap={4}>
                                        <PrimaryText>
                                            Назва: {legalEntity.companyName}
                                        </PrimaryText>
                                        <PrimaryText>
                                            Директор: {legalEntity.ceoName}
                                        </PrimaryText>
                                        <PrimaryText>
                                            Основна діяльність:{" "}
                                            {legalEntity.primaryActivity}
                                        </PrimaryText>
                                        <PrimaryText>
                                            {`Тип: ${
                                                TYPE_VOCABULARY[
                                                    legalEntity.type
                                                ] ?? TYPE_VOCABULARY.custom
                                            }`}
                                        </PrimaryText>
                                    </Flex>
                                ) : (
                                    <PrimaryText>
                                        За введеними даними не вдалося знайти
                                        юридичну особу. Спробуйте ще раз.
                                    </PrimaryText>
                                )}
                            </InfoCard>
                        )}
                    </Flex>

                    <Flex vertical gap={8}>
                        <BaseButton
                            size="large"
                            type="default"
                            onClick={onClose}
                            disabled={loading}
                        >
                            Скасувати
                        </BaseButton>

                        <BaseButton
                            size="large"
                            type="primary"
                            htmlType="submit"
                            loading={loading}
                        >
                            {status ? "Додати юридичну особу" : "Перевірити"}
                        </BaseButton>

                        <Button
                            size="small"
                            type="link"
                            onClick={handleCreateIllegal}
                            disabled={loading}
                        >
                            Створити тіньового контрагента
                        </Button>
                    </Flex>
                </Form>
            </FormProvider>
        </BaseModal>
    );
};
